"use strict";

import {
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MovieCreationIcon from "@mui/icons-material/MovieCreation";
import SettingsVoiceIcon from "@mui/icons-material/SettingsVoice";
import TranslateIcon from "@mui/icons-material/Translate";
import { ToastContainer, toast } from "react-toastify";

import { CREATE_PROJECT_BATCH, GET_USERS } from "../ApiEndpoints";
import ProjectSearch from "../Components/ProjectSearch";
import UserSelectAutocomplete from "../Components/UserSelectAutoComplete/index.js";
import { languages } from "../Data/languages.js";
import {
    VOICE_ENGINE_OPTIONS,
    getSupportedVoiceEngine,
    getVoiceEngineLabel,
} from "../Data/voiceEngines.js";
import {
    formatInputs,
    getLanguageName,
    getRoleOptionLabel,
    getTaskName,
} from "../utils/generalFunctions.js";
import { injectToken } from "../utils/injectToken";

const CREATE_PROJECTS_COLLECTIONS_STORAGE_KEY = "pendingCreateProjectsCollections";
const ORIGINAL_LANGUAGE_PLACEHOLDER = "Original Language";
const TARGET_LANGUAGE_PLACEHOLDER = "Target Language";

const roleOptions = [
    "transcriber",
    "translator",
    "performancedirection",
    "speakerfaceidentification",
    "lipqualityassurance",
    "qualityassurance",
];

const pageSx = {
    minHeight: "calc(100vh - 90px)",
    width: "100%",
    py: { xs: 4, md: 6 },
    px: { xs: 2, md: 4 },
    fontFamily: "var(--font-body)",
    background:
        "radial-gradient(circle at 20% 8%, rgba(0, 149, 199, 0.18), transparent 26%), radial-gradient(circle at 88% 28%, rgba(47, 188, 179, 0.16), transparent 28%), linear-gradient(135deg, #eaf7fa 0%, #e6f3ef 48%, #f7fbfc 100%)",
    backgroundAttachment: "fixed",
    position: "relative",
    overflowX: "hidden",
    "&::before": {
        content: "\"\"",
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        opacity: 0.45,
        backgroundImage:
            "linear-gradient(45deg, rgba(255,255,255,0.35) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.35) 75%, transparent 75%, transparent)",
        backgroundSize: "12px 12px",
    },
};

const shellSx = {
    width: "100%",
    maxWidth: "1440px",
    mx: "auto",
    position: "relative",
    zIndex: 1,
};

const glassCardSx = {
    borderRadius: "28px",
    border: "1px solid rgba(255,255,255,0.72)",
    background: "rgba(255,255,255,0.88)",
    boxShadow: "0 24px 80px rgba(13, 57, 78, 0.14)",
    backdropFilter: "blur(18px)",
};

const inputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "16px",
        backgroundColor: "rgba(255,255,255,0.84)",
        "& fieldset": {
            borderColor: "rgba(8, 72, 96, 0.16)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(0, 120, 160, 0.45)",
        },
        "&.Mui-focused": {
            boxShadow: "0 0 0 4px rgba(0, 124, 166, 0.10)",
        },
    },
};

const primaryButtonSx = {
    minHeight: 48,
    px: 3,
    borderRadius: "16px",
    fontWeight: 800,
    textTransform: "none",
    background: "linear-gradient(135deg, #00779f 0%, #00a4c7 100%)",
    boxShadow: "0 14px 28px rgba(0, 119, 159, 0.24)",
    "&:hover": {
        background: "linear-gradient(135deg, #00698d 0%, #0094b5 100%)",
        boxShadow: "0 18px 34px rgba(0, 119, 159, 0.28)",
    },
};

const outlineButtonSx = {
    minHeight: 48,
    px: 3,
    borderRadius: "16px",
    fontWeight: 800,
    textTransform: "none",
    color: "#00779f",
    borderColor: "rgba(0,119,159,0.28)",
};

const FieldLabel = ({ children }) => (
    <Typography fontWeight={800} mb={1} color="#243447">
        {children}
    </Typography>
);

const SectionCard = ({ icon: Icon, title, eyebrow, children, action }) => (
    <Paper elevation={0} sx={{ ...glassCardSx, p: { xs: 2.5, md: 3.5 } }}>
        <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            mb={3}
        >
            <Stack direction="row" spacing={1.5} alignItems="center">
                {Icon && (
                    <Avatar
                        sx={{
                            width: 42,
                            height: 42,
                            color: "#00779f",
                            bgcolor: "rgba(0, 119, 159, 0.10)",
                            borderRadius: "14px",
                        }}
                    >
                        <Icon fontSize="small" />
                    </Avatar>
                )}
                <Box>
                    {eyebrow && (
                        <Typography color="text.secondary" fontSize={12} fontWeight={800} textTransform="uppercase">
                            {eyebrow}
                        </Typography>
                    )}
                    <Typography fontSize={{ xs: 20, md: 24 }} fontWeight={900} color="#0e1d2b">
                        {title}
                    </Typography>
                </Box>
            </Stack>
            {action}
        </Stack>
        {children}
    </Paper>
);

const readCollectionsPayload = () => {
    try {
        const savedPayload = sessionStorage.getItem(CREATE_PROJECTS_COLLECTIONS_STORAGE_KEY);
        return savedPayload ? JSON.parse(savedPayload) : null;
    } catch (error) {
        console.error("Failed to read selected collections:", error);
        return null;
    }
};

const getCreateProjectsErrorMessage = (error) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Project batch creation failed";

const createDefaultSettings = () => ({
    orginalLang: ORIGINAL_LANGUAGE_PLACEHOLDER,
    targetLang: TARGET_LANGUAGE_PLACEHOLDER,
    voiceEngine: getSupportedVoiceEngine(),
    selectedSourceProject: null,
    members: [],
});

const createProjectItemFromCollection = (collection) => ({
    id: collection.id,
    collectionId: collection.id,
    collectionName: collection.name || collection.displayName || "Untitled Collection",
    projectName: formatInputs(collection.name || collection.displayName || "Untitled Collection"),
    followsDefault: true,
    orginalLang: ORIGINAL_LANGUAGE_PLACEHOLDER,
    targetLang: TARGET_LANGUAGE_PLACEHOLDER,
    voiceEngine: getSupportedVoiceEngine(),
    selectedSourceProject: null,
    members: [],
});

const MembersList = ({ members, onRemove }) => {
    if (!members.length) {
        return (
            <Typography fontSize={13.5} color="#64748b">
                No project members added.
            </Typography>
        );
    }

    return (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {members.map((member, index) => (
                <Chip
                    key={`${member.id}-${member.role}-${index}`}
                    label={`${member.name} - ${getTaskName(member.role)}`}
                    onDelete={onRemove ? () => onRemove(member, index) : undefined}
                    deleteIcon={onRemove ? <DeleteOutlineIcon /> : undefined}
                    sx={{ fontWeight: 800, bgcolor: "rgba(15,23,42,0.06)" }}
                />
            ))}
        </Stack>
    );
};

const CreateProjectsScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const routePayload = location.state;
    const savedPayload = readCollectionsPayload();
    const payload = routePayload?.collections ? routePayload : savedPayload;
    const selectedCollections = useMemo(() => payload?.collections || [], [payload]);
    const sortedLanguages = useMemo(
        () => [...languages].sort((a, b) => a.name.localeCompare(b.name)),
        []
    );

    const [defaultSettings, setDefaultSettings] = useState(createDefaultSettings);
    const [projectItems, setProjectItems] = useState(() =>
        selectedCollections.map(createProjectItemFromCollection)
    );
    const [defaultSelectedUser, setDefaultSelectedUser] = useState(null);
    const [defaultRole, setDefaultRole] = useState("select the role of the employee");
    const [selectedUsersByItem, setSelectedUsersByItem] = useState({});
    const [rolesByItem, setRolesByItem] = useState({});
    const [isCreatingProjects, setIsCreatingProjects] = useState(false);

    const updateDefaultSettings = (field, value) => {
        setDefaultSettings((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const getItemEffectiveSettings = (item) => {
        if (!item.followsDefault) {
            return item;
        }

        return {
            ...item,
            orginalLang: defaultSettings.orginalLang,
            targetLang: defaultSettings.targetLang,
            voiceEngine: defaultSettings.voiceEngine,
            selectedSourceProject: defaultSettings.selectedSourceProject,
            members: defaultSettings.members,
        };
    };

    const updateProjectItem = (itemId, updates) => {
        setProjectItems((prev) =>
            prev.map((item) => (
                item.id === itemId
                    ? { ...item, ...updates }
                    : item
            ))
        );
    };

    const toggleFollowsDefault = (item) => {
        if (item.followsDefault) {
            const effectiveSettings = getItemEffectiveSettings(item);
            updateProjectItem(item.id, {
                followsDefault: false,
                orginalLang: effectiveSettings.orginalLang,
                targetLang: effectiveSettings.targetLang,
                voiceEngine: effectiveSettings.voiceEngine,
                selectedSourceProject: effectiveSettings.selectedSourceProject,
                members: [...effectiveSettings.members],
            });
            return;
        }

        updateProjectItem(item.id, { followsDefault: true });
    };

    const addDefaultMember = () => {
        if (!defaultSelectedUser) {
            toast.error("Please select a valid employee");
            return;
        }

        if (defaultRole === "select the role of the employee") {
            toast.error("Please select a role for the user.");
            return;
        }

        setDefaultSettings((prev) => ({
            ...prev,
            members: [
                ...prev.members,
                {
                    id: defaultSelectedUser.id,
                    name: defaultSelectedUser.name,
                    role: defaultRole,
                },
            ],
        }));
        setDefaultSelectedUser(null);
        setDefaultRole("select the role of the employee");
    };

    const removeDefaultMember = (memberToRemove, memberIndex) => {
        setDefaultSettings((prev) => ({
            ...prev,
            members: prev.members.filter((member, index) => (
                index !== memberIndex ||
                member.id !== memberToRemove.id ||
                member.role !== memberToRemove.role
            )),
        }));
    };

    const addItemMember = (item) => {
        const selectedUser = selectedUsersByItem[item.id];
        const selectedRole = rolesByItem[item.id] || "select the role of the employee";

        if (!selectedUser) {
            toast.error("Please select a valid employee");
            return;
        }

        if (selectedRole === "select the role of the employee") {
            toast.error("Please select a role for the user.");
            return;
        }

        updateProjectItem(item.id, {
            members: [
                ...item.members,
                {
                    id: selectedUser.id,
                    name: selectedUser.name,
                    role: selectedRole,
                },
            ],
        });
        setSelectedUsersByItem((prev) => ({ ...prev, [item.id]: null }));
        setRolesByItem((prev) => ({ ...prev, [item.id]: "select the role of the employee" }));
    };

    const removeItemMember = (item, memberToRemove, memberIndex) => {
        updateProjectItem(item.id, {
            members: item.members.filter((member, index) => (
                index !== memberIndex ||
                member.id !== memberToRemove.id ||
                member.role !== memberToRemove.role
            )),
        });
    };

    const buildPreparedProjects = () =>
        projectItems.map((item) => {
            const effectiveSettings = getItemEffectiveSettings(item);

            return {
                collectionId: item.collectionId,
                collectionName: item.collectionName,
                projectName: item.projectName,
                assetCollectionId: item.collectionId,
                orginalLang: effectiveSettings.orginalLang,
                targetLang: effectiveSettings.targetLang,
                finalLang: effectiveSettings.targetLang === TARGET_LANGUAGE_PLACEHOLDER ? [] : [effectiveSettings.targetLang],
                voiceEngine: getSupportedVoiceEngine(effectiveSettings.voiceEngine),
                selectedSourceProject: effectiveSettings.selectedSourceProject,
                employees: effectiveSettings.members,
            };
        });

    const buildCreateProjectBatchPayload = (preparedProjects) => ({
        projects: preparedProjects.map((project) => ({
            name: project.projectName,
            employees: project.employees,
            orginalLang: project.orginalLang,
            finalLang: project.finalLang,
            voiceEngine: project.voiceEngine,
            assetCollectionId: project.assetCollectionId,
        })),
    });

    const handleCreateProjects = async () => {
        const preparedProjects = buildPreparedProjects();
        const missingProjectName = preparedProjects.some((project) => !project.projectName);
        const missingOriginalLanguage = preparedProjects.some((project) => project.orginalLang === ORIGINAL_LANGUAGE_PLACEHOLDER);
        const missingTargetLanguage = preparedProjects.some((project) => project.targetLang === TARGET_LANGUAGE_PLACEHOLDER);
        const missingAssetCollection = preparedProjects.some((project) => !project.assetCollectionId);

        if (missingProjectName) {
            toast.error("Every item needs a project name");
            return;
        }

        if (missingOriginalLanguage) {
            toast.error("Every item needs an original language");
            return;
        }

        if (missingTargetLanguage) {
            toast.error("Every item needs a target language");
            return;
        }

        if (missingAssetCollection) {
            toast.error("Every item needs a collection id");
            return;
        }

        setIsCreatingProjects(true);

        try {
            const headers = await injectToken();

            if (!headers?.Authorization) {
                throw new Error("Missing auth token");
            }

            const response = await axios.post(
                CREATE_PROJECT_BATCH,
                buildCreateProjectBatchPayload(preparedProjects),
                { headers }
            );
            const createdCount = response.data?.createdCount ?? preparedProjects.length;
            const failedCount = response.data?.failedCount ?? 0;

            sessionStorage.setItem(
                "createdProjectsBatchResponse",
                JSON.stringify(response.data)
            );
            sessionStorage.removeItem("preparedCreateProjects");
            toast.success(`Created ${createdCount} project${createdCount === 1 ? "" : "s"}${failedCount ? `, ${failedCount} failed` : ""}`);
        } catch (error) {
            console.error("Failed to create project batch:", error);
            toast.error(getCreateProjectsErrorMessage(error));
        } finally {
            setIsCreatingProjects(false);
        }
    };

    return (
        <Box sx={pageSx}>
            <ToastContainer position="top-center" />

            <Box sx={shellSx}>
                <Stack alignItems="center" textAlign="center" spacing={1.5} mb={4}>
                    <Avatar
                        sx={{
                            width: 58,
                            height: 58,
                            borderRadius: "18px",
                            color: "#00779f",
                            bgcolor: "rgba(255,255,255,0.88)",
                            boxShadow: "0 16px 45px rgba(0, 119, 159, 0.16)",
                        }}
                    >
                        <AutoAwesomeIcon />
                    </Avatar>
                    <Chip
                        label={`${projectItems.length} selected collection${projectItems.length === 1 ? "" : "s"}`}
                        sx={{
                            fontWeight: 800,
                            color: "#00779f",
                            bgcolor: "rgba(255,255,255,0.78)",
                            border: "1px solid rgba(0,119,159,0.12)",
                        }}
                    />
                    <Typography fontSize={{ xs: 34, md: 46 }} fontWeight={950} color="#0d1724">
                        Create Projects
                    </Typography>
                </Stack>

                <Stack spacing={3.5}>
                    <SectionCard
                        icon={SettingsVoiceIcon}
                        title="Default Item"
                        eyebrow="Shared settings"
                        action={(
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate("/file-upload")}
                                sx={outlineButtonSx}
                            >
                                Back to Collections
                            </Button>
                        )}
                    >
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={4}>
                                <FieldLabel>Original language</FieldLabel>
                                <FormControl fullWidth sx={inputSx}>
                                    <Select
                                        value={defaultSettings.orginalLang}
                                        onChange={(event) => updateDefaultSettings("orginalLang", event.target.value)}
                                    >
                                        <MenuItem value={ORIGINAL_LANGUAGE_PLACEHOLDER} disabled>
                                            Original Language
                                        </MenuItem>
                                        {sortedLanguages.map((language) => (
                                            <MenuItem key={language.code} value={language.code}>
                                                {language.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldLabel>Target language</FieldLabel>
                                <FormControl fullWidth sx={inputSx}>
                                    <Select
                                        value={defaultSettings.targetLang}
                                        onChange={(event) => updateDefaultSettings("targetLang", event.target.value)}
                                    >
                                        <MenuItem value={TARGET_LANGUAGE_PLACEHOLDER} disabled>
                                            Target Language
                                        </MenuItem>
                                        {sortedLanguages.map((language) => (
                                            <MenuItem key={language.code} value={language.code}>
                                                {language.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FieldLabel>Default voice engine</FieldLabel>
                                <FormControl fullWidth sx={inputSx}>
                                    <Select
                                        value={defaultSettings.voiceEngine}
                                        onChange={(event) => updateDefaultSettings("voiceEngine", event.target.value)}
                                    >
                                        {VOICE_ENGINE_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FieldLabel>Import characters</FieldLabel>
                                <ProjectSearch
                                    onProjectSelect={(project) => updateDefaultSettings("selectedSourceProject", project || null)}
                                />
                                {defaultSettings.selectedSourceProject?.name && (
                                    <Chip
                                        label={`Selected source project: ${defaultSettings.selectedSourceProject.name}`}
                                        sx={{ mt: 1.5, fontWeight: 800, color: "#00779f", bgcolor: "rgba(0,119,159,0.10)" }}
                                    />
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FieldLabel>Project members</FieldLabel>
                                <Grid container spacing={1.5} alignItems="center">
                                    <Grid item xs={12} md={5}>
                                        <UserSelectAutocomplete
                                            onUserSelect={setDefaultSelectedUser}
                                            existingUsers={defaultSettings.members}
                                            fetchUsersUrl={GET_USERS}
                                            applyWidth={false}
                                            selectedUser={defaultSelectedUser}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <FormControl fullWidth sx={inputSx}>
                                            <Select
                                                value={defaultRole}
                                                onChange={(event) => setDefaultRole(event.target.value)}
                                            >
                                                <MenuItem value="select the role of the employee" disabled>
                                                    Select employee role
                                                </MenuItem>
                                                {roleOptions.map((role) => (
                                                    <MenuItem key={role} value={role}>
                                                        {getRoleOptionLabel(role)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={addDefaultMember} sx={primaryButtonSx}>
                                            Add
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <MembersList members={defaultSettings.members} onRemove={removeDefaultMember} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </SectionCard>

                    <SectionCard
                        icon={MovieCreationIcon}
                        title="Project Items"
                        eyebrow="Selected collections"
                        action={(
                            <Button
                                variant="contained"
                                startIcon={<MovieCreationIcon />}
                                disabled={projectItems.length === 0 || isCreatingProjects}
                                onClick={handleCreateProjects}
                                sx={primaryButtonSx}
                            >
                                {isCreatingProjects ? "Creating..." : "Create Projects"}
                            </Button>
                        )}
                    >
                        {projectItems.length === 0 ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    borderRadius: "22px",
                                    textAlign: "center",
                                    border: "1px dashed rgba(7,43,59,0.14)",
                                    bgcolor: "rgba(248,250,252,0.72)",
                                }}
                            >
                                <Typography fontWeight={900} color="#334155">
                                    No collections selected
                                </Typography>
                                <Typography color="#64748b" fontSize={14}>
                                    Go back to Collections and select at least one item.
                                </Typography>
                            </Paper>
                        ) : (
                            <Stack spacing={2.5}>
                                {projectItems.map((item) => {
                                    const effectiveSettings = getItemEffectiveSettings(item);
                                    const isFollowingDefault = item.followsDefault;

                                    return (
                                        <Paper
                                            key={item.id}
                                            elevation={0}
                                            sx={{
                                                p: { xs: 2, md: 2.5 },
                                                borderRadius: "22px",
                                                border: `1px solid ${isFollowingDefault ? "rgba(7,43,59,0.08)" : "rgba(0,119,159,0.30)"}`,
                                                bgcolor: isFollowingDefault ? "rgba(255,255,255,0.74)" : "rgba(255,255,255,0.94)",
                                            }}
                                        >
                                            <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" mb={2}>
                                                <Box minWidth={0}>
                                                    <Typography fontWeight={950} color="#142234" fontSize={20} noWrap>
                                                        {item.collectionName}
                                                    </Typography>
                                                    <Typography color="#64748b" fontSize={13.5}>
                                                        {isFollowingDefault ? "Using Default Item settings" : "Using custom item settings"}
                                                    </Typography>
                                                </Box>
                                                <FormControlLabel
                                                    control={(
                                                        <Checkbox
                                                            checked={isFollowingDefault}
                                                            onChange={() => toggleFollowsDefault(item)}
                                                            sx={{ color: "#00779f", "&.Mui-checked": { color: "#00779f" } }}
                                                        />
                                                    )}
                                                    label="Follow Default Item"
                                                />
                                            </Stack>

                                            <Grid container spacing={2.2}>
                                                <Grid item xs={12} md={4}>
                                                    <FieldLabel>Project name</FieldLabel>
                                                    <TextField
                                                        fullWidth
                                                        value={item.projectName}
                                                        onChange={(event) => updateProjectItem(item.id, { projectName: formatInputs(event.target.value) })}
                                                        sx={inputSx}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FieldLabel>Original language</FieldLabel>
                                                    <FormControl fullWidth sx={inputSx}>
                                                        <Select
                                                            value={effectiveSettings.orginalLang}
                                                            disabled={isFollowingDefault}
                                                            onChange={(event) => updateProjectItem(item.id, { orginalLang: event.target.value })}
                                                        >
                                                            <MenuItem value={ORIGINAL_LANGUAGE_PLACEHOLDER} disabled>
                                                                Original Language
                                                            </MenuItem>
                                                            {sortedLanguages.map((language) => (
                                                                <MenuItem key={language.code} value={language.code}>
                                                                    {language.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FieldLabel>Target language</FieldLabel>
                                                    <FormControl fullWidth sx={inputSx}>
                                                        <Select
                                                            value={effectiveSettings.targetLang}
                                                            disabled={isFollowingDefault}
                                                            onChange={(event) => updateProjectItem(item.id, { targetLang: event.target.value })}
                                                        >
                                                            <MenuItem value={TARGET_LANGUAGE_PLACEHOLDER} disabled>
                                                                Target Language
                                                            </MenuItem>
                                                            {sortedLanguages.map((language) => (
                                                                <MenuItem key={language.code} value={language.code}>
                                                                    {language.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FieldLabel>Default voice engine</FieldLabel>
                                                    <FormControl fullWidth sx={inputSx}>
                                                        <Select
                                                            value={effectiveSettings.voiceEngine}
                                                            disabled={isFollowingDefault}
                                                            onChange={(event) => updateProjectItem(item.id, { voiceEngine: event.target.value })}
                                                        >
                                                            {VOICE_ENGINE_OPTIONS.map((option) => (
                                                                <MenuItem key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <Chip
                                                        icon={<SettingsVoiceIcon />}
                                                        label={getVoiceEngineLabel(effectiveSettings.voiceEngine)}
                                                        sx={{ mt: 1, fontWeight: 800, bgcolor: "rgba(0,119,159,0.10)", color: "#00779f" }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FieldLabel>Import characters</FieldLabel>
                                                    {isFollowingDefault ? (
                                                        <Chip
                                                            icon={<ContentCopyIcon />}
                                                            label={effectiveSettings.selectedSourceProject?.name || "Following default import"}
                                                            sx={{ fontWeight: 800, bgcolor: "rgba(15,23,42,0.06)" }}
                                                        />
                                                    ) : (
                                                        <ProjectSearch
                                                            onProjectSelect={(project) => updateProjectItem(item.id, { selectedSourceProject: project || null })}
                                                        />
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <FieldLabel>Project members</FieldLabel>
                                                    {isFollowingDefault ? (
                                                        <MembersList members={effectiveSettings.members} />
                                                    ) : (
                                                        <Stack spacing={1.5}>
                                                            <UserSelectAutocomplete
                                                                onUserSelect={(user) => setSelectedUsersByItem((prev) => ({ ...prev, [item.id]: user }))}
                                                                existingUsers={item.members}
                                                                fetchUsersUrl={GET_USERS}
                                                                applyWidth={false}
                                                                selectedUser={selectedUsersByItem[item.id] || null}
                                                            />
                                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                                                <FormControl fullWidth sx={inputSx}>
                                                                    <Select
                                                                        value={rolesByItem[item.id] || "select the role of the employee"}
                                                                        onChange={(event) => setRolesByItem((prev) => ({ ...prev, [item.id]: event.target.value }))}
                                                                    >
                                                                        <MenuItem value="select the role of the employee" disabled>
                                                                            Select employee role
                                                                        </MenuItem>
                                                                        {roleOptions.map((role) => (
                                                                            <MenuItem key={role} value={role}>
                                                                                {getRoleOptionLabel(role)}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                                <IconButton
                                                                    onClick={() => addItemMember(item)}
                                                                    sx={{ color: "#00779f", bgcolor: "rgba(0,119,159,0.08)", borderRadius: "14px" }}
                                                                >
                                                                    <AddIcon />
                                                                </IconButton>
                                                            </Stack>
                                                            <MembersList
                                                                members={item.members}
                                                                onRemove={(member, index) => removeItemMember(item, member, index)}
                                                            />
                                                        </Stack>
                                                    )}
                                                </Grid>
                                            </Grid>

                                            <Divider sx={{ my: 2, borderColor: "rgba(7,43,59,0.08)" }} />
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Chip icon={<TranslateIcon />} label={`Original: ${getLanguageName(effectiveSettings.orginalLang)}`} sx={{ fontWeight: 800 }} />
                                                <Chip icon={<TranslateIcon />} label={`Target: ${getLanguageName(effectiveSettings.targetLang)}`} sx={{ fontWeight: 800 }} />
                                                {isFollowingDefault && (
                                                    <Chip label="Inherited" sx={{ fontWeight: 900, color: "#00779f", bgcolor: "rgba(0,119,159,0.10)" }} />
                                                )}
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        )}
                    </SectionCard>
                </Stack>
            </Box>
        </Box>
    );
};

export default CreateProjectsScreen;
